import type { Action } from '@troyai/actions/data-access';
import type {
  HealthSummary,
  HealthSummaryMedicationItem,
} from '@troyai/portal/health-timeline/data-access';
import {
  DEMO_ACTORS,
  DEMO_START,
  SCRIPTED_ID_BASE,
  demoMemberId,
  demoPharmacyName,
  storyIso,
} from '@troyai/portal/investor-demo/core';
import type { Patient } from '@troyai/patients/data-access';

import { ACTION_IDS } from './ids';
import { CONDITIONS, DEMO_KNOWLEDGE_COVERAGE, seedMedicationItems } from './timeline.fixture';

/**
 * Margaret "Peggy" Hollis: born 1956-03-02 (70), Charlotte NC, Troy Medicare MA since 2024-01-01.
 * Care manager Dana Whitfield, PCP Dr. Amanda Cole. Her cardiologist is Dr. Ravi Shah and her
 * orthopedist Dr. Michael Patel; neither is part of the Patient model.
 *
 * Dates in comments are the scripted ones, written for Tue Oct 13; every date in the data goes
 * through `storyIso`, which moves it onto the real story day.
 */

const SEED_DAY = storyIso(DEMO_START).slice(0, 10);
const FULL_NAME = 'Margaret Hollis';
const PHARMACY_ID = 4101;

const MEMBER = {
  full_name: FULL_NAME,
  dob: '1956-03-02',
  first_name: 'Margaret',
  last_name: 'Hollis',
  middle_name: '',
};

function action(
  overrides: Partial<Action> & Pick<Action, 'global_id' | 'title' | 'status'>
): Action {
  return {
    action_template_id: SCRIPTED_ID_BASE + 9650,
    timestamp: storyIso('2026-10-07T14:40:00'),
    due_date: storyIso('2026-10-07'),
    member_assigned_to: demoMemberId(),
    reference_id: 'care-coordination',
    name: 'Care coordination',
    fee: '0',
    fee_schedule: 'Care coordination',
    fee_schedule_image: '',
    start_date: storyIso('2026-10-06'),
    template_id: SCRIPTED_ID_BASE + 9650,
    description: '',
    fee_schedule_id: SCRIPTED_ID_BASE + 9650,
    created_on: storyIso('2026-10-06T16:05:00'),
    pharmacy_name: demoPharmacyName(),
    pharmacy_id: PHARMACY_ID,
    scheduling_enabled: false,
    first_scheduled_by: '',
    scheduled_by: '',
    message_count: 0,
    member: MEMBER,
    updated_at: storyIso('2026-10-07T14:40:00'),
    completion_time_max: 0,
    completion_time_min: 0,
    unread_message_count: 0,
    resources: [],
    last_hra_completed_on: null,
    extra_data: [],
    related_actions: [],
    latest_form_submission_id: null,
    is_follow_up_for_action_id: null,
    member_name: FULL_NAME,
    ...overrides,
  };
}

/**
 * The fax to Dr. Patel (`SCRIPTED_ID_BASE + 9601`, completed Oct 7) and the ride benefit set-up
 * (`SCRIPTED_ID_BASE + 9602`, in review).
 */
export function seedActions(): Action[] {
  return [
    action({
      global_id: ACTION_IDS.faxToPatel,
      title:
        'Fax to Dr. Patel (orthopedics): discontinuation request + topical diclofenac alternative',
      status: 'Completed',
      timestamp: storyIso('2026-10-07T14:40:00'),
      updated_at: storyIso('2026-10-07T14:40:00'),
    }),
    action({
      global_id: ACTION_IDS.rideBenefit,
      action_template_id: SCRIPTED_ID_BASE + 9651,
      template_id: SCRIPTED_ID_BASE + 9651,
      title: 'Ride benefit set-up',
      status: 'In Review',
      due_date: storyIso('2026-10-30'),
      timestamp: storyIso('2026-10-06T16:20:00'),
      updated_at: storyIso('2026-10-06T16:20:00'),
      created_on: storyIso('2026-10-06T16:20:00'),
    }),
  ];
}

export function seedPatient(): Patient {
  return {
    global_id: demoMemberId(),
    first_name: 'Margaret',
    middle_name: '',
    last_name: 'Hollis',
    dob: '1956-03-02',
    deceased_on: null,
    member_id: 'TM900000041',
    member_id_original: null,
    beneficiary_id: '4HG6TK2MA91',
    gender_identity: null,
    sexual_identity: null,
    ethnicity: null,
    race: null,
    gender: 'Female',
    pharmacy_name: demoPharmacyName(),
    pharmacy_global_id: PHARMACY_ID,
    current_enrollment_state: {
      global_id: demoMemberId(),
      enrolled_date: storyIso('2024-01-01'),
      enrolled: true,
      current_enrollment_status: 'Approved',
      future_enrollment_status: 'Approved',
      current_state: {
        application_date: storyIso('2023-11-02'),
        beneficiary_id: '4HG6TK2MA91',
        current_enrollment_status: 'Approved',
        disenrollment_reason: '',
        dob: '1956-03-02',
        enrolled: true,
        enrolled_date: storyIso('2024-01-01'),
        first_name: 'Margaret',
        future_enrollment_status: 'Approved',
        last_name: 'Hollis',
        lis_copay_cat: '',
        lis_effective_date: '',
        lis_end_date: '',
        lis_premium_subsidy: 0,
        lis_subsidy_level: '',
        lis_subsidy_source: '',
        member_id: 'TM900000041',
        middle_initial: '',
        pbp: 1,
        source_id: 'troy-ma',
        gender: 'Female',
      },
      plan_type: 'MAPD',
      lis_premium_subsidy: '',
      lis_copay_cat: '',
      lis_effective_date: '',
      lis_end_date: '',
      lis_subsidy_level: '',
      lis_subsidy_source: '',
    },
    member_address: [
      {
        type: 'address',
        id: 'demo-address-1',
        hash: 'demo-address-1',
        address_line_1: '2418 Providence Road',
        city: 'Charlotte',
        state: 'NC',
        zip_code: '28211',
        county: 'Mecklenburg',
        address_type: 'Physical',
        contact_info_note: null,
        contact_info_note_id: null,
      },
    ],
    member_phone: [
      {
        type: 'phone',
        id: 'demo-phone-1',
        hash: 'demo-phone-1',
        phone_number: '7045550177',
        phone_type: 'primary',
        contact_info_note: null,
        contact_info_note_id: null,
      },
    ],
    actions: seedActions(),
    critical_comments: [],
    member_gaps: [],
    care_manager: { name: DEMO_ACTORS.dana.name, phone: '7045550142' },
    pcp: {
      name: 'Dr. Amanda Cole',
      phone: '7045550163',
      npi: '1043558872',
      fax: '7045550164',
      facility_name: 'Myers Park Family Medicine',
    },
    risk_score: 2.84,
    pharmacy: { npi: '1366402914', phone: '7045550110' },
    encounters: [],
    rx_group: null,
    rx_bin: null,
    rx_pcn: null,
    er_visits_prior_210_days: 1,
    ip_visits_prior_12_months: 0,
    frail_flag: false,
    eight_plus_meds_flag: true,
    bh_flag: false,
    ckd_diagnosis: true,
    diabetes_diagnosis: true,
    heart_failure_diagnosis: true,
    strata: 2,
    tier: 2,
    tier_metadata: { is_overridden: false, overridden_at: null, overridden_by: null },
  };
}

/**
 * The Health summary as of the story start: 9 active medications, the 5 conditions, 1 ED visit in
 * the last 90 days (2 visits: the Jul 20 cardiology visit is inside the window too), coverage
 * active since 2024-01-01. The diclofenac fill that appears after "+3 days" is not counted here.
 * `medications` defaults to the care story's; the pharmacy story passes its own, whose entries
 * carry other ids.
 */
export function seedHealthSummary(
  medications: HealthSummaryMedicationItem[] = seedMedicationItems()
): HealthSummary {
  const conditions = [
    CONDITIONS.chf,
    CONDITIONS.diabetes,
    CONDITIONS.hypertension,
    CONDITIONS.ckd,
    CONDITIONS.osteoarthritis,
  ];
  return {
    member_global_id: demoMemberId(),
    as_of: SEED_DAY,
    as_of_knowledge: storyIso(DEMO_START),
    is_current: true,
    coverage: {
      status: 'active',
      status_label: 'Active coverage',
      headline: 'Enrolled in Troy Medicare Advantage since Jan 1, 2024',
      plan: 'Troy Medicare Advantage',
      since: storyIso('2024-01-01'),
      extra_help: false,
    },
    medications: {
      available: true,
      active_count: medications.length,
      headline: `${medications.length} active medications`,
      items: medications,
    },
    visits: {
      available: true,
      count_last_90_days: 2,
      emergency_room_count_last_90_days: 1,
      hospital_stay_count_last_90_days: 0,
      headline: '2 medical visits in the last 90 days',
      high_acuity_headline: '1 ER visit · 0 hospital stays',
      last_visit: storyIso('2026-08-19'),
    },
    conditions: {
      available: true,
      recorded_count_last_12_months: conditions.length,
      headline: `${conditions.slice(0, 3).join(', ')} and ${conditions.length - 3} more`,
      items: conditions,
    },
    knowledge_coverage: DEMO_KNOWLEDGE_COVERAGE,
  };
}
